import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Table,
  Sheet,
  Chip,
  Input,
  CircularProgress,
  Alert,
  Select,
  Option,
  Grid,
} from '@mui/joy';
import {
  ArrowsClockwise as Refresh,
  DownloadSimple as Download,
  Warning,
} from '@phosphor-icons/react';
import { apiAccountingClient } from '../../lib/apiAccounting';
import { useNavigate } from 'react-router-dom';

interface InvoiceDetail {
  invoice_number: string;
  customer: string;
  customer_id: number | null;
  issue_date: string;
  due_date: string | null;
  days_past_due: number;
  total_amount: number;
  paid_amount: number;
  outstanding_amount: number;
}

interface AgingBucket {
  total: number;
  count: number;
  invoices: InvoiceDetail[];
}

interface CustomerSummary {
  customer: string;
  customer_id: number | null;
  current: number;
  '1_30': number;
  '31_60': number;
  '61_90': number;
  '91_120': number;
  'over_120': number;
  total: number;
}

interface ARAgingData {
  as_of_date: string;
  aging_buckets: {
    current: AgingBucket;
    '1_30': AgingBucket;
    '31_60': AgingBucket;
    '61_90': AgingBucket;
    '91_120': AgingBucket;
    'over_120': AgingBucket;
  };
  customer_summary: CustomerSummary[];
  grand_total: number;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const ARAgingReport: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ARAgingData | null>(null);
  const [asOfDate, setAsOfDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [viewMode, setViewMode] = useState<'summary' | 'detail'>('summary');

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiAccountingClient.getARAgingReport(asOfDate);
      setData(response);
    } catch (err) {
      setError('Failed to load A/R Aging Report');
      console.error('Error fetching report:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [asOfDate]);

  const handleExport = () => {
    // TODO: Implement CSV export
    console.log('Export to CSV');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="danger" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return <Alert color="warning">No data available</Alert>;
  }

  const bucketColumns = [
    { key: 'current', label: 'Current', color: 'success' },
    { key: '1_30', label: '1-30 Days', color: 'success' },
    { key: '31_60', label: '31-60 Days', color: 'warning' },
    { key: '61_90', label: '61-90 Days', color: 'warning' },
    { key: '91_120', label: '91-120 Days', color: 'danger' },
    { key: 'over_120', label: 'Over 120 Days', color: 'danger' },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 3 }}>
        <Typography level="h2" sx={{ fontSize: { xs: '1.25rem', md: undefined } }}>Accounts Receivable Aging Report</Typography>
        <Box sx={{ display: 'flex', gap: 1, '& > button': { flex: { xs: 1, sm: '0 0 auto' } } }}>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Download />}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startDecorator={<Refresh />}
            onClick={fetchReport}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <CollapsibleFilters><Card sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 2 }, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Input
            type="date"
            placeholder="As of Date"
            value={asOfDate}
            onChange={(e) => setAsOfDate(e.target.value)}
            sx={{ width: { xs: '100%', sm: 200 } }}
          />
          <Select
            value={viewMode}
            onChange={(_, value) => value && setViewMode(value as 'summary' | 'detail')}
            sx={{ minWidth: { xs: 0, sm: 150 } }}
          >
            <Option value="summary">Summary View</Option>
            <Option value="detail">Detailed View</Option>
          </Select>
        </Box>
      </Card></CollapsibleFilters>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {bucketColumns.map(({ key, label, color }) => {
          const bucket = data.aging_buckets[key as keyof typeof data.aging_buckets];
          const percentage = data.grand_total > 0
            ? (bucket.total / data.grand_total) * 100
            : 0;

          return (
            <Grid key={key} xs={12} sm={6} md={2}>
              <Card>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  {label}
                </Typography>
                <Typography level="h3">
                  {formatCurrency(bucket.total)}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Chip size="sm" variant="soft" color={color as any}>
                    {bucket.count} invoices
                  </Chip>
                  <Typography level="body-xs">
                    {percentage.toFixed(1)}%
                  </Typography>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Grand Total */}
      <Card sx={{ mb: 3, backgroundColor: 'primary.softBg' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1 }}>
          <Typography level="h4">Total Outstanding</Typography>
          <Typography level="h2" sx={{ color: 'primary.main', fontSize: { xs: '1.5rem', md: undefined } }}>
            {formatCurrency(data.grand_total)}
          </Typography>
        </Box>
      </Card>

      {/* Customer Summary Table */}
      {viewMode === 'summary' && (
        <Card>
          <Typography level="h4" sx={{ mb: 2 }}>Customer Summary</Typography>
          <Sheet sx={{ overflow: 'auto' }}>
            <Table
              hoverRow
              sx={{
                minWidth: 880,
                '& thead th': { fontWeight: 'bold' },
                '& td': { fontSize: 'sm' }
              }}
            >
              <thead>
                <tr>
                  <th>Customer</th>
                  <th style={{ textAlign: 'right' }}>Current</th>
                  <th style={{ textAlign: 'right' }}>1-30 Days</th>
                  <th style={{ textAlign: 'right' }}>31-60 Days</th>
                  <th style={{ textAlign: 'right' }}>61-90 Days</th>
                  <th style={{ textAlign: 'right' }}>91-120 Days</th>
                  <th style={{ textAlign: 'right' }}>Over 120</th>
                  <th style={{ textAlign: 'right', fontWeight: 'bold' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.customer_summary.map((customer, index) => (
                  <tr
                    key={index}
                    style={{ cursor: 'pointer' }}
                    onClick={() => customer.customer_id && navigate(`/crm/contacts/${customer.customer_id}`)}
                  >
                    <td>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {customer.customer}
                        {customer.total > 10000 && (
                          <Warning size={16} color="var(--joy-palette-warning-500)" />
                        )}
                      </Box>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {customer.current > 0 ? formatCurrency(customer.current) : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {customer['1_30'] > 0 ? formatCurrency(customer['1_30']) : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {customer['31_60'] > 0 ? formatCurrency(customer['31_60']) : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {customer['61_90'] > 0 ? formatCurrency(customer['61_90']) : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {customer['91_120'] > 0 ? formatCurrency(customer['91_120']) : '-'}
                    </td>
                    <td style={{ textAlign: 'right', color: 'danger.main' }}>
                      {customer['over_120'] > 0 ? formatCurrency(customer['over_120']) : '-'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                      {formatCurrency(customer.total)}
                    </td>
                  </tr>
                ))}
                {data.customer_summary.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '40px' }}>
                      No outstanding invoices
                    </td>
                  </tr>
                )}
              </tbody>
              {data.customer_summary.length > 0 && (
                <tfoot>
                  <tr style={{ fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' }}>
                    <td>Grand Total</td>
                    <td style={{ textAlign: 'right' }}>
                      {formatCurrency(data.aging_buckets.current.total)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {formatCurrency(data.aging_buckets['1_30'].total)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {formatCurrency(data.aging_buckets['31_60'].total)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {formatCurrency(data.aging_buckets['61_90'].total)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {formatCurrency(data.aging_buckets['91_120'].total)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {formatCurrency(data.aging_buckets['over_120'].total)}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {formatCurrency(data.grand_total)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </Sheet>
        </Card>
      )}

      {/* Detailed Invoice View */}
      {viewMode === 'detail' && (
        <Box>
          {bucketColumns.map(({ key, label }) => {
            const bucket = data.aging_buckets[key as keyof typeof data.aging_buckets];
            if (bucket.count === 0) return null;

            return (
              <Card key={key} sx={{ mb: 3 }}>
                <Typography level="h4" sx={{ mb: 2 }}>
                  {label} ({bucket.count} invoices - {formatCurrency(bucket.total)})
                </Typography>
                <Sheet sx={{ overflow: 'auto' }}>
                  <Table size="sm" hoverRow sx={{ minWidth: 720 }}>
                    <thead>
                      <tr>
                        <th>Invoice #</th>
                        <th>Customer</th>
                        <th>Issue Date</th>
                        <th>Due Date</th>
                        <th>Days Past Due</th>
                        <th style={{ textAlign: 'right' }}>Total</th>
                        <th style={{ textAlign: 'right' }}>Paid</th>
                        <th style={{ textAlign: 'right' }}>Outstanding</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bucket.invoices.map((invoice, idx) => (
                        <tr key={idx}>
                          <td>{invoice.invoice_number}</td>
                          <td>{invoice.customer}</td>
                          <td>{formatDate(invoice.issue_date)}</td>
                          <td>{formatDate(invoice.due_date)}</td>
                          <td>
                            <Chip
                              size="sm"
                              variant="soft"
                              color={invoice.days_past_due > 90 ? 'danger' : invoice.days_past_due > 30 ? 'warning' : 'success'}
                            >
                              {invoice.days_past_due} days
                            </Chip>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {formatCurrency(invoice.total_amount)}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {formatCurrency(invoice.paid_amount)}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                            {formatCurrency(invoice.outstanding_amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Sheet>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
